import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Mail, User, Calendar, Clock, Bell, MessageSquare, ArrowLeft, ArrowRight, Upload, CheckCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Separator } from '../ui/Separator';
import { Badge } from '../ui/Badge';
import { useAnonymousReviewContext } from './AnonymousReviewProvider';
import { AnonymousReviewService } from '../../lib/anonymousReviewService';
import { useAuth } from '@clerk/clerk-react';

const AnonymousStep3: React.FC = () => {
  const navigate = useNavigate();
  const { data, error, setError, isLoading, setIsLoading } = useAnonymousReviewContext();
  const [isCreating, setIsCreating] = useState(false);
  const { getToken } = useAuth();

  const formatReminderText = (reminder: { type: string; customDate?: string; customTime?: string }) => {
    if (reminder.type === 'custom' && reminder.customDate && reminder.customTime) {
      const customDateTime = new Date(`${reminder.customDate}T${reminder.customTime}`);
      return `Custom: ${customDateTime.toLocaleString('en-US', {
        weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', 
        hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
      })}`;
    }
    switch (reminder.type) {
      case '3-days': return '3 days before deadline';
      case '1-day': return '1 day before deadline';
      case '3-hours': return '3 hours before deadline';
      default: return reminder.type;
    }
  };

  const handleCreateReview = async () => {
    try {
      setIsCreating(true);
      setError(null);

      console.log('Creating anonymous review with data:', data);
      console.log('Files array:', data.files);
      console.log('Files length:', data.files?.length);
      console.log('File metadata:', data.fileMetadata);
      console.log('Has files from landing page:', data.hasFilesFromLandingPage);

      // Note: CloudConvert conversion will work without access token in anonymous flow
      // The Supabase function uses service role authentication

      // Prepare the data for the service
      const serviceParams = {
        // Step 1: File upload & basic info
        files: data.files,
        title: data.title,
        description: data.description,
        creatorEmail: data.creatorEmail,
        preuploadedStoragePath: (data as any).preuploadedStoragePath,
        preuploadedFileName: (data as any).preuploadedFileName,
        preuploadedFileType: (data as any).preuploadedFileType,
        
        // Step 2: Review details & settings
        reviewerEmail: data.reviewerEmail,
        reviewerFirstName: data.reviewerFirstName,
        reviewerLastName: data.reviewerLastName,
        deadlineEnabled: data.deadlineEnabled,
        deadline: data.deadline,
        deadlineTime: data.deadlineTime,
        reminderEnabled: data.reminderEnabled,
        reminders: data.reminders,
        customInstructions: data.customInstructions,
        timeZone: data.timeZone
      };

      console.log('Calling AnonymousReviewService.createAnonymousReview with params:', serviceParams);

      // Call the actual backend service
      const result = await AnonymousReviewService.createAnonymousReview(serviceParams);
      
      console.log('Anonymous review created successfully:', result);

      // Update the context with the results
      data.tempReviewId = result.tempReviewId;
      data.anonymousReviewId = result.anonymousReviewId;
      data.reviewLink = result.reviewLink;

      // Navigate to registration
      navigate('../register');
    } catch (err: any) {
      console.error('Error creating review:', err);
      const errorMessage = err.message || 'Failed to create review. Please try again.';
      setError(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          Step 3: Preview & Confirm
        </h1>
        <p className="text-muted-foreground">
          Review your information and create the review
        </p>
      </div>

      <div className="space-y-6">
        {/* Files Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Files & Title
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Review Title:</h4>
              <p className="text-lg font-semibold text-primary">{data.title || 'No title set'}</p>
            </div>
            
            <Separator />
            
            <div>
              <h4 className="font-medium mb-2">Files:</h4>
              { (data as any).preuploadedStoragePath ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 bg-green-50 p-3 rounded">
                    <FileText className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium">{(data as any).preuploadedFileName || 'Uploaded file'}</span>
                    <span className="text-xs text-muted-foreground">Already uploaded from landing page</span>
                  </div>
                </div>
              ) : data.files.length > 0 ? (
                <div className="space-y-2">
                  {data.files.map((file, index) => (
                    <div key={index} className="flex items-center gap-2 bg-gray-50 p-3 rounded">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">{file.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                  ))}
                </div>
              ) : data.fileMetadata && data.fileMetadata.length > 0 ? (
                <div className="space-y-2">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                    <p className="text-xs text-yellow-700 mb-1">
                      ⚠️ Files need to be re-uploaded (browser limitation)
                    </p>
                    <p className="text-xs text-yellow-600">
                      Previously selected files are shown below. Files will be uploaded during review creation.
                    </p>
                  </div>
                  {data.fileMetadata.map((file, index) => (
                    <div key={index} className="flex items-center gap-2 bg-gray-50 p-3 rounded opacity-75">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-muted-foreground">{file.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                        Will re-upload
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm">No files uploaded - you can upload them after registration</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* People Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              People
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Your Email:
                </h4>
                <p className="text-sm bg-gray-50 p-2 rounded">{data.creatorEmail || 'Not set'}</p>
              </div>
              
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Reviewer Email:
                </h4>
                <p className="text-sm bg-gray-50 p-2 rounded">{data.reviewerEmail || 'Not set'}</p>
              </div>
            </div>
            
            {(data.reviewerFirstName || data.reviewerLastName) && (
              <div>
                <h4 className="font-medium mb-2">Reviewer Name:</h4>
                <p className="text-sm bg-gray-50 p-2 rounded">
                  {[data.reviewerFirstName, data.reviewerLastName].filter(Boolean).join(' ') || 'Not provided'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Deadline & Reminders Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Deadline & Reminders
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Deadline:</h4>
              {data.deadlineEnabled && data.deadline && data.deadlineTime ? (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(`${data.deadline}T${data.deadlineTime}`).toLocaleDateString()}
                  </Badge>
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(`${data.deadline}T${data.deadlineTime}`).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </Badge>
                  <span className="text-xs text-muted-foreground">({data.timeZone})</span>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No deadline set</p>
              )}
            </div>

            {data.reminderEnabled && data.reminders.length > 0 && (
              <>
                <Separator />
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    Reminders ({data.reminders.length}):
                  </h4>
                  <div className="space-y-2">
                    {data.reminders.map((reminder, index) => (
                      <div key={index} className="flex items-center gap-2 bg-blue-50 p-2 rounded text-sm">
                        <Bell className="w-3 h-3 text-blue-600" />
                        {formatReminderText(reminder)}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Instructions Section */}
        {data.customInstructions && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Custom Instructions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 p-4 rounded">
                <p className="text-sm whitespace-pre-wrap">{data.customInstructions}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error Display */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-600">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Summary & Actions */}
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <CheckCircle className="w-5 h-5" />
              Ready to Create Review
            </CardTitle>
            <CardDescription className="text-green-700">
              Your review request will be created and you'll be asked to register to access the review links.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm text-green-700">
              <p>✓ Review title: <strong>{data.title}</strong></p>
              <p>✓ Files: <strong>{data.files.length > 0 ? `${data.files.length} file(s)` : 'Upload after registration'}</strong></p>
              <p>✓ Reviewer: <strong>{data.reviewerEmail}</strong></p>
              {data.deadlineEnabled && data.deadline && (
                <p>✓ Deadline: <strong>{new Date(`${data.deadline}T${data.deadlineTime}`).toLocaleDateString()}</strong></p>
              )}
              {data.reminderEnabled && data.reminders.length > 0 && (
                <p>✓ Reminders: <strong>{data.reminders.length} reminder(s) set</strong></p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => navigate('../step-2')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Step 2
          </Button>
          <Button 
            onClick={handleCreateReview} 
            disabled={isCreating}
            className="bg-green-600 hover:bg-green-700"
          >
            {isCreating ? (
              <>Creating Review...</>
            ) : (
              <>
                Create Review
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AnonymousStep3; 